import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDialogOverlayElement } from "@/components/dialog-overlay-context";
import { SelectClearControl } from "@/components/select-clear-control";
import { ChevronDown, Check, Search } from "lucide-react";

type SearchableSelectOption = {
  value: string;
  label: string;
  sublabel?: string;
};

export function SearchableSelect({
  options,
  value,
  onValueChange,
  onClear,
  clearLabel = "Clear selection",
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  "data-testid": testId,
}: {
  options: SearchableSelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  onClear?: () => void;
  clearLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  "data-testid"?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [panelPos, setPanelPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dialogOverlayElement = useDialogOverlayElement();

  const computeRect = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const btnRect = btn.getBoundingClientRect();
    // Panel height: search input (~40px) + up to 200px list + padding (~16px).
    const PANEL_H = 260;
    const GAP = 4;

    // When position:fixed lives inside a CSS-transformed ancestor (Radix Dialog uses
    // translate-x/y-[-50%]), that element becomes the fixed containing block.
    // Offsets must therefore be relative to the dialog's bounding rect, not the viewport.
    // Flip decision is always viewport-based so the panel isn't prematurely
    // flipped just because the button sits near the bottom of the dialog box.
    const dialogEl = btn.closest("[role='dialog']");
    if (dialogEl) {
      const dr = dialogEl.getBoundingClientRect();
      const spaceBelow = dr.bottom - btnRect.bottom - GAP;
      const spaceAbove = btnRect.top - dr.top - GAP;
      const openUpward = spaceBelow < PANEL_H && spaceAbove > spaceBelow;
      if (openUpward) {
        setPanelPos({ bottom: dr.bottom - btnRect.top + GAP, left: btnRect.left - dr.left, width: btnRect.width });
      } else {
        setPanelPos({ top: btnRect.bottom - dr.top + GAP, left: btnRect.left - dr.left, width: btnRect.width });
      }
    } else {
      const spaceBelow = window.innerHeight - btnRect.bottom - GAP;
      const spaceAbove = btnRect.top - GAP;
      const openUpward = spaceBelow < PANEL_H && spaceAbove > spaceBelow;
      if (openUpward) {
        setPanelPos({ bottom: window.innerHeight - btnRect.top + GAP, left: btnRect.left, width: btnRect.width });
      } else {
        setPanelPos({ top: btnRect.bottom + GAP, left: btnRect.left, width: btnRect.width });
      }
    }
  };

  const handleOpen = () => {
    computeRect();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  // Outside-click + scroll/resize handling
  useEffect(() => {
    if (!open) return;

    const raf = requestAnimationFrame(computeRect);

    function onPointerDown(e: PointerEvent) {
      if (buttonRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      handleClose();
    }
    function onScrollOrResize() {
      computeRect();
    }

    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const filtered = options.filter(
    (o) =>
      o.label.toLowerCase().includes(search.toLowerCase()) ||
      o.value.toLowerCase().includes(search.toLowerCase()) ||
      (o.sublabel && o.sublabel.toLowerCase().includes(search.toLowerCase())),
  );

  const selectedOption = options.find((o) => o.value === value);

  const panel =
    open && panelPos ? (
      <div
        ref={panelRef}
        className="pointer-events-auto rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
        data-searchable-dropdown-portal
        style={{
          position: "fixed",
          zIndex: 9999,
          ...(panelPos.top !== undefined ? { top: panelPos.top } : { bottom: panelPos.bottom }),
          left: panelPos.left,
          width: panelPos.width,
        }}
      >
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-7 text-sm"
            autoFocus
          />
        </div>
        <div className="max-h-[200px] overflow-y-auto space-y-0.5">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-3">No results found</p>
          ) : (
            filtered.map((option) => (
              <button
                key={option.value}
                type="button"
                title={option.label}
                className="flex items-center justify-between w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer text-left"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onValueChange(option.value);
                  handleClose();
                }}
                data-testid={testId ? `${testId}-option-${option.value}` : undefined}
              >
                <span className="truncate">{option.label}</span>
                <Check className={`w-4 h-4 shrink-0 ml-2 ${value === option.value ? "opacity-100" : "opacity-0"}`} />
              </button>
            ))
          )}
        </div>
      </div>
    ) : null;

  return (
    <div className="relative w-full">
      <SelectClearControl
        clearLabel={clearLabel}
        testId={testId}
        onClear={onClear ? () => {
          onClear();
          handleClose();
        } : undefined}
      >
        <Button
          ref={buttonRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          onPointerDown={(e) => {
            e.preventDefault();
            if (open) {
              handleClose();
            } else {
              handleOpen();
            }
          }}
          data-testid={testId}
        >
          <span className={onClear ? "truncate flex-1 text-left" : "truncate"} style={{ paddingRight: "var(--select-clear-padding)" }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown className="w-4 h-4 text-muted-foreground ml-2 shrink-0" />
        </Button>
      </SelectClearControl>
      {panel && dialogOverlayElement ? createPortal(panel, dialogOverlayElement) : panel}
    </div>
  );
}
